import { type LocaleMessageLoaders, localeForLanguage } from '@shared/i18n';
import { useUserStore } from '@shared/stores/user';
import { storeToRefs } from 'pinia';
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

/**
 * Keeps the app's i18n locale in sync with the logged-in user's preferred
 * language, fetching a locale's messages the first time it's needed. Call once
 * from each app's root component.
 */
export const useUserLocale = (loaders: LocaleMessageLoaders) => {
  const { locale, availableLocales, setLocaleMessage } = useI18n({
    useScope: 'global'
  });
  const { currentS3User } = storeToRefs(useUserStore());
  const loadingLocale = ref(false);

  watch(
    () => currentS3User.value?.preferred_language,
    async (language, _, onCleanup) => {
      let stale = false;
      onCleanup(() => (stale = true));

      const nextLocale = localeForLanguage(language);
      if (nextLocale !== 'en' && !availableLocales.includes(nextLocale)) {
        loadingLocale.value = true;
        try {
          const messages = await loaders[nextLocale]();
          setLocaleMessage(nextLocale, messages);
        } finally {
          loadingLocale.value = false;
        }
        // The language changed again while messages were loading
        if (stale) return;
      }

      locale.value = nextLocale;
      document.documentElement.lang = nextLocale;
    },
    { immediate: true }
  );

  return { locale, loadingLocale };
};
